import { faMessage } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ChatSummary } from "app/modules/aiAssistant/AIAssistantSlice";
import { deleteChatSession, renameChatSession } from "app/modules/aiAssistant/AIAssistantThunks";
import ChatOptionsMenu from "app/modules/aiAssistant/page/chatsList/components/ChatOptionsMenu";
import { ASSISTIMO_ROUTE, getAssistimoChatRoute } from "app/modules/aiAssistant/shared/assistimoRoutes";
import "./ChatListItem.scss";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useThunkDispatch } from "setup/redux/thunkDispatch";

type Props = {
  chat: ChatSummary;
  isActive: boolean;
};

const ChatListItem = ({ chat, isActive }: Props) => {
  const { chatSessionID, chatName } = chat;
  const [renamingChat, setRenamingChat] = useState(false);
  const [draftChatName, setDraftChatName] = useState(chatName);
  const navigate = useNavigate();
  const thunkDispatch = useThunkDispatch();
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Set by Escape so the blur that follows doesn't save
  const cancelRenameRef = useRef(false);

  useEffect(() => {
    if (renamingChat) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [renamingChat]);

  const startRename = () => {
    cancelRenameRef.current = false;
    setDraftChatName(chatName);
    setRenamingChat(true);
  };

  const finishRename = () => {
    setRenamingChat(false);
    const newChatName = draftChatName.trim();
    if (cancelRenameRef.current || newChatName === "" || newChatName === chatName) return;

    thunkDispatch(renameChatSession({ chatSessionID, newChatName }));
  };

  return (
    <li className={`assistimo-chat-list-item ${isActive ? "assistimo-chat-list-item--active" : ""}`}>
      {renamingChat ? (
        <form
          className="assistimo-chat-list-item__rename-form"
          onSubmit={(e) => {
            e.preventDefault();
            inputRef.current?.blur();
          }}
        >
          <FontAwesomeIcon icon={faMessage} className="assistimo-chat-list-item__icon" />
          <input
            data-test-id={`assistimo-chats-list__name-input-${chatSessionID}`}
            type="text"
            className="assistimo-chat-list-item__name-input"
            aria-label="Chat name"
            value={draftChatName}
            onChange={(event) => setDraftChatName(event.currentTarget.value)}
            onBlur={finishRename}
            ref={inputRef}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                cancelRenameRef.current = true;
                inputRef.current?.blur();
              }
            }}
          />
        </form>
      ) : (
        <button
          data-test-id={`assistimo-chats-list__open-chat-button-${chatSessionID}`}
          type="button"
          className="assistimo-chat-list-item__open-button"
          aria-current={isActive ? "page" : undefined}
          onClick={() => navigate(getAssistimoChatRoute(chatSessionID), { replace: true })}
        >
          <FontAwesomeIcon icon={faMessage} className="assistimo-chat-list-item__icon" />
          <span className="assistimo-chat-list-item__name" title={chatName}>
            {chatName}
          </span>
        </button>
      )}
      <ChatOptionsMenu
        chatSessionID={chatSessionID}
        onRename={startRename}
        onDelete={async () => {
          const result = await thunkDispatch(deleteChatSession(chatSessionID));
          if (deleteChatSession.fulfilled.match(result) && isActive) {
            navigate(ASSISTIMO_ROUTE, { replace: true });
          }
        }}
      />
    </li>
  );
};

export default ChatListItem;
